import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import {
  clearCompletedAskPopups,
  dismissAskPopup,
  removeCompletedAskPopup,
  resetAskPopupDismissals
} from '../../../../actions/aiAnalyticsActions';
import { AI_ANALYTICS_PATH, ASK_OUTCOMES } from '../../../../constants/aiAnalytics';
import { AI_COLORS, ASK_OUTCOME_LABELS } from '../aiAnalyticsConstants';

import AskStatusIndicator from './AskStatusIndicator';
import InlineProgress from './InlineProgress';

function AskPopupCard({ ask, onClose, children }) {
  return (
    <Paper elevation={6} sx={{ position: 'relative', p: 2, borderRadius: 2 }}>
      <IconButton size="small" onClick={onClose} title="Close" sx={{ position: 'absolute', top: 4, right: 4 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
      <Typography variant="subtitle2" noWrap sx={{ pr: 4, color: AI_COLORS.brand }}>
        {ask.threadTitle || 'AI Analytics'}
      </Typography>
      {ask.question && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 1 }}>
          {ask.question}
        </Typography>
      )}
      {children}
    </Paper>
  );
}

export default function AskProgressPopup() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { pendingAsks, pendingOps, completedAsks, dismissedAskPopups } = useSelector(state => state.aiAnalytics);
  const onAiAnalyticsPage = location.pathname === AI_ANALYTICS_PATH;

  useEffect(
    function() {
      if (onAiAnalyticsPage) {
        dispatch(resetAskPopupDismissals());
        if (completedAsks.length) {
          dispatch(clearCompletedAskPopups());
        }
      }
    },
    [onAiAnalyticsPage, completedAsks.length, dispatch]
  );

  if (onAiAnalyticsPage) {
    return null;
  }

  const runningAsks = Object.values(pendingAsks).filter(
    ask => ask.queryId && !dismissedAskPopups.includes(ask.queryId)
  );
  const runningOps = Object.values(pendingOps).filter(op => !dismissedAskPopups.includes(op.key));
  if (runningAsks.length === 0 && runningOps.length === 0 && completedAsks.length === 0) {
    return null;
  }

  return (
    <Box
      sx={theme => ({
        position: 'fixed',
        left: 16,
        bottom: 16,
        zIndex: theme.zIndex.snackbar,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        width: 340,
        maxWidth: 'calc(100vw - 32px)'
      })}
    >
      {runningAsks.map(function(ask) {
        return (
          <AskPopupCard key={ask.queryId} ask={ask} onClose={() => dispatch(dismissAskPopup(ask.queryId))}>
            <AskStatusIndicator phase={ask.phase} />
          </AskPopupCard>
        );
      })}
      {runningOps.map(function(op) {
        return (
          <AskPopupCard key={op.key} ask={{ threadTitle: op.title }} onClose={() => dispatch(dismissAskPopup(op.key))}>
            <InlineProgress label={op.label} dense />
          </AskPopupCard>
        );
      })}
      {completedAsks.map(function(ask) {
        const succeeded = ask.outcome === ASK_OUTCOMES.FINISHED;
        return (
          <AskPopupCard key={ask.queryId} ask={ask} onClose={() => dispatch(removeCompletedAskPopup(ask.queryId))}>
            <Box sx={{ width: '100%', minWidth: 0 }}>
              <Typography variant="body2" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                {ask.doneLabel || ASK_OUTCOME_LABELS[ask.outcome] || ASK_OUTCOME_LABELS[ASK_OUTCOMES.FINISHED]}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={100}
                sx={{
                  height: 5,
                  borderRadius: 2,
                  backgroundColor: succeeded ? AI_COLORS.brandSurface : AI_COLORS.errorSurface,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: succeeded ? AI_COLORS.brand : AI_COLORS.errorText
                  }
                }}
              />
            </Box>
          </AskPopupCard>
        );
      })}
    </Box>
  );
}
